import { useState, useMemo, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Modal } from 'components/UI/Modal/Modal'
import { TextInput } from 'components/FormUtils/TextInput'
import { TriggerActionStep } from './TriggerActionStep'
import { cleanAutomationFormDataPayload } from './utils'
import { AutomationAttachment } from 'models/automation'
import { useCycleValidation } from './useCycleValidation'
import { CycleValidationErrorCard } from './CycleValidationErrorCard'

// Mixed attachment type for form handling
export type FormAttachment = File | AutomationAttachment

const automationFormSchema = z
  .object({
    name: z.string().min(1, 'Automation name is required'),
    description: z.string().optional(),
    trigger_type: z.string().min(1, 'Trigger is required'),
    action_type: z.string().min(1, 'Action is required'),
    // Trigger target fields
    trigger_target_user_id: z.number().optional().nullable(),
    trigger_target_stage_id: z.number().optional().nullable(),
    // Action target fields
    target_user_id: z.number().optional().nullable(),
    target_stage_id: z.number().optional().nullable(),
    target_tag_id: z.number().optional().nullable(),
    target_campaign_id: z.number().optional().nullable(),
    content: z.string().optional().nullable(),
    attachments: z.array(z.union([z.instanceof(File), z.any()])).default([]),
  })
  .superRefine((data, ctx) => {
    // Validate field_changed_account_owner trigger requires trigger_target_user_id
    if (
      data.trigger_type === 'field_changed_account_owner' &&
      !data.trigger_target_user_id
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Account owner selection is required',
        path: ['trigger_target_user_id'],
      })
    }

    // Validate field_changed_sales_stage trigger requires trigger_target_stage_id
    if (
      data.trigger_type === 'field_changed_sales_stage' &&
      !data.trigger_target_stage_id
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sales stage selection is required',
        path: ['trigger_target_stage_id'],
      })
    }

    // Validate notify_user action requires target_user_id
    if (data.action_type === 'notify_user' && !data.target_user_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'User selection is required',
        path: ['target_user_id'],
      })
    }

    // Validate change_sales_stage action requires target_stage_id
    if (data.action_type === 'change_sales_stage' && !data.target_stage_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Sales stage selection is required',
        path: ['target_stage_id'],
      })
    }

    // Validate change_account_owner action requires target_user_id
    if (data.action_type === 'change_account_owner' && !data.target_user_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Account owner selection is required',
        path: ['target_user_id'],
      })
    }

    // Validate add_tag action requires target_tag_id
    if (data.action_type === 'add_tag' && !data.target_tag_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Tag selection is required',
        path: ['target_tag_id'],
      })
    }

    // Validate add_to_campaign action requires target_campaign_id
    if (data.action_type === 'add_to_campaign' && !data.target_campaign_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campaign selection is required',
        path: ['target_campaign_id'],
      })
    }

    // Validate remove_from_campaign action requires target_campaign_id
    if (
      data.action_type === 'remove_from_campaign' &&
      !data.target_campaign_id
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Campaign selection is required',
        path: ['target_campaign_id'],
      })
    }

    // Validate add_company_note action requires note content
    if (data.action_type === 'add_company_note' && !data.content) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Note content is required',
        path: ['content'],
      })
    }
  })

export type AutomationFormData = z.infer<typeof automationFormSchema> & {
  attachments: FormAttachment[]
}

export interface AutomationApiActionsData {
  action_type: string
  target_user_id?: number | null
  target_stage_id?: number | null
  target_tag_id?: number | null
  target_campaign_id?: number | null
  order_index: number
  content?: string | null
  attachments?: File[]
}

// Type for automation data from API (database structure)
export interface AutomationApiData {
  id: number
  name: string
  description?: string
  is_active: boolean
  condition_groups?: Array<{
    logical_operator: string
    order_index: number
    conditions: Array<{
      trigger_type: string
      target_user_id?: number
      target_stage_id?: number
      order_index: number
    }>
  }>
  actions?: Array<AutomationApiActionsData>
  attachments?: AutomationAttachment[]
}

export interface AutomationModalProps {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  submitApiAction: (values: AutomationFormData) => Promise<unknown>
  automationToEdit?: AutomationApiData
  confirmButtonText?: string
  title: string
  description: string
  loading?: boolean
}

export function AutomationModal({
  show,
  handleClose,
  onSubmitSuccess,
  submitApiAction,
  automationToEdit,
  confirmButtonText = 'Create',
  title,
  description,
  loading = false,
}: AutomationModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {
    isValidating,
    validationError,
    validateNewAutomation,
    validateUpdateAutomation,
    clearValidationError,
  } = useCycleValidation()

  // Transform API data to form data for editing
  const defaultValues = useMemo((): AutomationFormData => {
    if (!automationToEdit) {
      return {
        name: '',
        description: '',
        trigger_type: '',
        action_type: '',
        trigger_target_user_id: undefined,
        trigger_target_stage_id: undefined,
        target_user_id: undefined,
        target_stage_id: undefined,
        target_tag_id: undefined,
        target_campaign_id: undefined,
        content: undefined,
        attachments: [],
      }
    }

    // Extract trigger and action from nested structure
    const firstCondition =
      automationToEdit.condition_groups?.[0]?.conditions?.[0]
    const firstAction = automationToEdit.actions?.[0]

    return {
      name: automationToEdit.name,
      description: automationToEdit.description || '',
      trigger_type: firstCondition?.trigger_type || '',
      action_type: firstAction?.action_type || '',
      trigger_target_user_id: firstCondition?.target_user_id,
      trigger_target_stage_id: firstCondition?.target_stage_id,
      target_user_id: firstAction?.target_user_id,
      target_stage_id: firstAction?.target_stage_id,
      target_tag_id: firstAction?.target_tag_id,
      target_campaign_id: firstAction?.target_campaign_id,
      content: firstAction?.content,
      attachments: automationToEdit.attachments || [],
    }
  }, [automationToEdit])

  const formMethods = useForm<AutomationFormData>({
    resolver: zodResolver(automationFormSchema),
    defaultValues,
  })

  const { handleSubmit, reset, setError, watch } = formMethods

  // Clear validation errors when trigger or action type changes
  const watchedTriggerType = watch('trigger_type')
  const watchedActionType = watch('action_type')

  // Keep track of the last validated combination to avoid clearing errors immediately after validation
  const [lastValidatedCombination, setLastValidatedCombination] = useState<{
    trigger: string
    action: string
  } | null>(null)

  useEffect(() => {
    // Only clear validation error if the user actually changed the values after validation
    if (validationError && watchedTriggerType && watchedActionType) {
      const currentCombination = {
        trigger: watchedTriggerType,
        action: watchedActionType,
      }

      // If this is the same combination we just validated and got an error for, don't clear it
      if (
        lastValidatedCombination &&
        lastValidatedCombination.trigger === currentCombination.trigger &&
        lastValidatedCombination.action === currentCombination.action
      ) {
        return
      }
      clearValidationError()
      setLastValidatedCombination(null)
    }
  }, [
    watchedTriggerType,
    watchedActionType,
    validationError,
    clearValidationError,
    lastValidatedCombination,
  ])

  // Reset form when automation data changes
  useEffect(() => {
    if (automationToEdit) {
      const formValues: AutomationFormData = {
        name: automationToEdit.name,
        description: automationToEdit.description || '',
        trigger_type:
          automationToEdit.condition_groups?.[0]?.conditions?.[0]
            ?.trigger_type || '',
        action_type: automationToEdit.actions?.[0]?.action_type || '',
        trigger_target_user_id:
          automationToEdit.condition_groups?.[0]?.conditions?.[0]
            ?.target_user_id,
        trigger_target_stage_id:
          automationToEdit.condition_groups?.[0]?.conditions?.[0]
            ?.target_stage_id,
        target_user_id: automationToEdit.actions?.[0]?.target_user_id,
        target_stage_id: automationToEdit.actions?.[0]?.target_stage_id,
        target_tag_id: automationToEdit.actions?.[0]?.target_tag_id,
        target_campaign_id: automationToEdit.actions?.[0]?.target_campaign_id,
        content: automationToEdit.actions?.[0]?.content,
        attachments: automationToEdit.attachments || [], // Load existing attachments for editing
      }
      reset(formValues)
    }
  }, [automationToEdit, reset])

  const onSubmit = handleSubmit(async (values) => {
    // Clear any previous validation errors
    clearValidationError()

    setIsSubmitting(true)
    try {
      // Perform cycle validation before submitting
      const isUpdate = !!automationToEdit
      let isValid = false

      // Prepare target data for validation
      const targetData = {
        trigger_target_user_id: values.trigger_target_user_id,
        trigger_target_stage_id: values.trigger_target_stage_id,
        target_user_id: values.target_user_id,
        target_stage_id: values.target_stage_id,
        target_tag_id: values.target_tag_id,
        target_campaign_id: values.target_campaign_id,
      }

      if (isUpdate && automationToEdit?.id) {
        isValid = await validateUpdateAutomation(
          automationToEdit.id,
          values.trigger_type,
          values.action_type,
          targetData
        )
      } else {
        isValid = await validateNewAutomation(
          values.trigger_type,
          values.action_type,
          targetData
        )
      }

      // Track the combination we just validated
      setLastValidatedCombination({
        trigger: values.trigger_type,
        action: values.action_type,
      })

      // If validation failed, don't proceed with submission
      if (!isValid) {
        return
      }

      // Proceed with normal form submission
      const cleanedValues = cleanAutomationFormDataPayload(values)
      await submitApiAction(cleanedValues)
      reset()
      clearValidationError()
      onSubmitSuccess?.()
      handleClose()
    } catch (error) {
      // Handle API errors here if needed
      setError('name', { message: 'Failed to save automation' })
    } finally {
      setIsSubmitting(false)
    }
  })

  const _handleClose = () => {
    reset()
    clearValidationError()
    handleClose()
  }

  return (
    <Modal
      open={show}
      onOpenChange={(open) => {
        if (!open) _handleClose()
      }}
      title={title}
      description={description}
      acceptButtonText={confirmButtonText}
      onAccept={onSubmit}
      onCancel={_handleClose}
      loading={isSubmitting || loading || isValidating}
      size="lg"
    >
      <FormProvider {...formMethods}>
        <fieldset disabled={loading || isSubmitting || isValidating}>
          <div className="space-y-6">
            <div className="space-y-4">
              <TextInput
                name="name"
                label="Automation Name"
                control={formMethods.control}
                placeholder="Enter automation name"
                noMargin
              />

              <TextInput
                name="description"
                label="Description"
                control={formMethods.control}
                placeholder="Enter automation description"
                optional
              />
            </div>

            <TriggerActionStep automationId={automationToEdit?.id} />

            {/* Cycle Validation Error Display */}
            {validationError && (
              <CycleValidationErrorCard
                validationError={validationError}
                onDismiss={clearValidationError}
              />
            )}
          </div>
        </fieldset>
      </FormProvider>
    </Modal>
  )
}
